/**
 * THE MODEL'S CONFIDENCE, AS THREE DOTS AND A WORD (P39 — audit S2-15, reference PD8)
 *
 * =========================================================================
 * WHAT WAS THERE
 * =========================================================================
 * `DRA-2026-00053` read **"DE 0.95"** beside *Mail written in*, and
 * `ReplyLanguagePanel` read **"DE (confidence 0.95)"** — a raw model output as a
 * bare decimal, one card away from the match-score badge, which is drawn as a
 * percentage. Two of the three numbers CLAUDE.md invariant 6 keeps apart, on one
 * screen, in two notations, and neither labelled as to what it measures.
 *
 * PD8: *"Confidence is three dots and a word, not a percentage. A model's
 * percentage is not a probability and invites false precision."*
 *
 * =========================================================================
 * THE NUMBER IS NOT SHOWN AT ALL, NOT EVEN IN THE TOOLTIP
 * =========================================================================
 * Moving `0.95` into a `title` would keep exactly the false precision the rule
 * exists to remove, one hover later. So the tooltip says what the reading is
 * ABOUT — which is the half the old screen never said — and points at the ledger
 * for the figure itself. `Dra.AiDecisionLog` has carried it since P3 and
 * `/audit-trail` renders it; the exact number is evidence, and evidence belongs
 * in the ledger rather than in a fact card.
 *
 * =========================================================================
 * THESE BANDS ARE PRESENTATION, AND THAT IS STATED RATHER THAN HIDDEN
 * =========================================================================
 * The match score's bands are the SERVER's (`api/src/shared/match-score.ts`, the
 * one place they live — invariant 6) and this component must never be mistaken
 * for that arrangement. The extraction confidence has no such module: the
 * pipeline's own gate is `DocumentTypeMaster.ConfidenceThresholdsJson.language`,
 * which is data in the database, seeded **0.75**, and the browser is never sent
 * it.
 *
 * So the low boundary is that number and not a round third: a reading the
 * pipeline would not have trusted to choose the reply's wording
 * (`resolveReplyLanguage`, level 2) is exactly the reading a person should look
 * at, and it reads **Low · check this**. What this component must not do — and
 * does not — is claim to BE that gate: it colours a number somebody else
 * decided with, it decides nothing, and nothing branches on its answer.
 */
import type { ReactNode } from 'react';

export type ConfidenceLevel = 'high' | 'medium' | 'low';

/**
 * The seeded `language` threshold of `DocumentTypeMaster` (0.75) is the LOW
 * boundary, for the reason in the header. HIGH is 0.90, which is the same
 * document type's `docType` and `isDocumentRequest` threshold — the figure this
 * pipeline already treats as "sure".
 */
export const CONFIDENCE_HIGH = 0.9;
export const CONFIDENCE_LOW = 0.75;

export function confidenceLevel(value: number): ConfidenceLevel {
    if (value >= CONFIDENCE_HIGH) return 'high';
    if (value >= CONFIDENCE_LOW) return 'medium';
    return 'low';
}

const WORDS: Record<ConfidenceLevel, string> = { high: 'High', medium: 'Medium', low: 'Low' };
const FILLED: Record<ConfidenceLevel, number> = { high: 3, medium: 2, low: 1 };

/**
 * §8's rule, applied: amber is *a person has to act*, and a low reading is the
 * only one of the three that asks anything of anybody. High and Medium are
 * neutral — they are saying "carry on", which is not a colour.
 */
const TINT: Record<ConfidenceLevel, string> = {
    high: 'text-muted',
    medium: 'text-muted',
    low: 'text-warn-ink',
};
const DOT_ON: Record<ConfidenceLevel, string> = {
    high: 'bg-muted',
    medium: 'bg-muted',
    low: 'bg-warn',
};

export interface ConfidenceProps {
    /** The model's own 0–1 reading. `null` renders nothing — there was no reading. */
    value: number | null | undefined;
    /**
     * What the number is about, as a noun phrase finishing "how sure the agent
     * was about …". It is the labelling half of S2-15 and is required for that
     * reason: an unlabelled marker beside a value is read as being about the
     * value.
     */
    about: string;
    className?: string;
}

export function Confidence({ value, about, className = '' }: ConfidenceProps): ReactNode {
    if (value === null || value === undefined) return null;
    const level = confidenceLevel(value);
    const filled = FILLED[level];

    return (
        <span
            className={`inline-flex items-center gap-1 whitespace-nowrap align-middle text-xs ${TINT[level]} ${className}`.trim()}
            title={`How sure the agent was about ${about}. It is the model's own reading and not a probability, `
                + 'which is why it is shown as a band rather than a figure. The exact reading is in the audit '
                + 'trail. It is a different number from the match score on a customer or article, which measures '
                + 'how alike two names are.'}
        >
            <span aria-hidden="true" className="inline-flex items-center gap-0.5">
                {[0, 1, 2].map(i => (
                    <span
                        key={i}
                        className={`h-1.5 w-1.5 rounded-full ${i < filled ? DOT_ON[level] : 'bg-hair'}`}
                    />
                ))}
            </span>
            {/* The word is the statement; the dots repeat it in a shape, because
                colour and a count never work alone (§8). */}
            <span>{WORDS[level]}{level === 'low' ? ' · check this' : ''}</span>
        </span>
    );
}
